import { ReactComponent as Ubicacion } from '../../assets/icons/pointer.svg';
import { ReactComponent as Teleconsulta } from '../../assets/icons/agendar.svg';
import { ReactComponent as Usuario } from '../../assets/icons/usuario.svg';
import SvgIcon from '../../utils/Icon';
import { useNavigate } from 'react-router-dom';
import { FaCheckCircle } from 'react-icons/fa';
import { useDataUser } from '../../context/dataUserContext';
import { formatPersonName, formatServiceName } from '../../utils/formatServiceName';

const formatService = (service) => formatServiceName(service);

const expertiseLines = (description) => {
    const text = String(description || '').trim();
    if (!text) return [];
    return text
        .split(/\n+|,\s*/)
        .map((line) => line.trim().replace(/\.$/, ''))
        .filter(Boolean)
        .slice(0, 3)
        .map((line) => {
            const lower = line.toLocaleLowerCase('es-CO');
            return lower.charAt(0).toLocaleUpperCase('es-CO') + lower.slice(1);
        });
};

const DoctorPhoto = ({ doctor, name }) => (
    <div className="w-24 h-24 rounded-full bg-slate-100 flex-shrink-0 border border-slate-200 overflow-hidden flex items-center justify-center">
        {doctor.urlProfessionalPhoto ? (
            <img
                src={doctor.urlProfessionalPhoto}
                alt={`Foto de ${name}`}
                className="w-full h-full rounded-full object-cover"
                referrerPolicy="no-referrer"
                onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                }}
            />
        ) : null}
        <div className={`w-full h-full rounded-full items-center justify-center ${doctor.urlProfessionalPhoto ? 'hidden' : 'flex'}`}>
            <SvgIcon Icon={Usuario} className="w-10 h-10 text-slate-400" />
        </div>
    </div>
);

const DoctorCard = ({ doctor, onAgendar }) => {
    const navigate = useNavigate();
    const { doctorId } = useDataUser();
    const isOwnProfile = doctorId && doctor._id && String(doctorId) === String(doctor._id);
    const name = formatPersonName(doctor.fullName);
    const lines = expertiseLines(doctor.description);
    const verificado = Boolean(doctor.isVerified);

    const onHandleAgendar = () => {
        if (onAgendar) {
            onAgendar(doctor);
            return;
        }
        navigate('../teleconsultas', { state: { doctor } });
    };

    const canSchedule = (service) => {
        if (!service) return false;
        const normalizeService = (s) => s.toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\d+/g, '')
            .replace(/_/g, ' ')
            .replace(/-/g, ' ')
            .trim();
        const serviceNormalized = normalizeService(service);
        const schedulableServices = [
            'medicina familiar',
            'medicina general',
            'terapia fisica',
            'terapeuta fisica',
            'fisioterapeuta',
            'psicologia',
            'psicologo',
            'nutricion',
            'nutricionista',
            'fonoaudiologia',
            'fonoaudiologo',
            'enfermeria',
            'terapeuta respiratorio',
            'terapeuta respiratoria',
            'terapeuta ocupacional',
        ];
        return schedulableServices.some((schedulable) => {
            const schedulableNormalized = normalizeService(schedulable);
            return serviceNormalized.includes(schedulableNormalized)
                || schedulableNormalized.includes(serviceNormalized);
        });
    };

    const dep = doctor.department;
    const ciudad = dep?.ciudades?.[0];
    const departamento = dep?.departamento;
    const ubicacion = ciudad && departamento
        ? `${ciudad}, ${departamento}`
        : departamento
            ? `${departamento}, Colombia`
            : null;
    const puedeAgendar = onAgendar
        ? true
        : doctor.membershipPlan === 'premium' || canSchedule(doctor.service);
    const mostrarAgenda = puedeAgendar && !isOwnProfile;

    return (
        <article
            className={`relative flex h-full w-full flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${onAgendar ? 'cursor-pointer' : ''}`}
            onClick={mostrarAgenda && onAgendar ? onHandleAgendar : undefined}
        >
            <div className="flex items-start gap-4">
                <DoctorPhoto doctor={doctor} name={name} />
                <div className="min-w-0 flex-1 pt-1">
                    <h3 className="flex items-start gap-1.5 text-lg font-bold leading-tight text-slate-900">
                        <span>{name}</span>
                        {verificado && (
                            <FaCheckCircle className="mt-1 h-4 w-4 shrink-0 text-emerald-500" title="Profesional verificado" />
                        )}
                    </h3>
                    <p className="mt-1 text-sm text-slate-600">{formatService(doctor.service)}</p>
                </div>
            </div>

            {lines.length > 0 && (
                <ul className="mt-4 space-y-2">
                    {lines.map((line) => (
                        <li key={line} className="flex gap-2 text-sm leading-snug text-slate-700">
                            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#1f94c2]" />
                            <span>{line}</span>
                        </li>
                    ))}
                </ul>
            )}

            {ubicacion && (
                <div className="mt-4 flex items-center text-xs text-slate-500">
                    <SvgIcon Icon={Ubicacion} className="mr-1.5 h-4 w-4 shrink-0 text-slate-400" />
                    <span>{ubicacion}</span>
                </div>
            )}

            {mostrarAgenda && (
                <button
                    type="button"
                    className="mt-4 flex w-full items-center justify-center rounded-xl bg-[#1f94c2] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1a7ba8]"
                    onClick={(event) => {
                        event.stopPropagation();
                        onHandleAgendar();
                    }}
                >
                    <SvgIcon Icon={Teleconsulta} className="mr-2 h-4 w-4" />
                    Agendar cita
                </button>
            )}
        </article>
    );
};

export default DoctorCard;
